import {
  OrderCreationType,
  SDK_AVAILABILITY_IDENTIFIER_TYPES,
  SDK_AVAILABILITY_REPORTED_REASONS,
} from '@ekhodealer/ekho-common';
import { EKHO_EVENTS } from '@ekhodealer/ekho-common/analytics';
import { parseGaClientId } from '@ekhodealer/ekho-common/utils';

import type {
  MarketingAttribution,
  SdkAvailabilityIdentifierType,
  SdkAvailabilityOptions,
  SdkAvailabilityResult,
  SdkBuildToOrderCartOptions,
  SdkCartVehicle,
  SdkCreateCartOptions,
  SdkCreateCartResult,
  SdkInventoryCartOptions,
  SdkMarketingAttribution,
  SdkValidateCartOptions,
} from '@ekhodealer/ekho-common';
import type { SdkApiClient } from '../../config/server/apiClient';

import { cartEndpoints } from '../../config/server/apiEndpoints';
import { getConsentedIdentity, isTrackingAllowed } from '../../loader/consent';
import { track, trackEventBeacon } from '../../utils/analytics';
import { readAffiliateCode } from '../../utils/attribution';
import { readCookie } from '../../utils/cookies';

/**
 * Convert SdkMarketingAttribution (null fields + ISO capturedAt) to
 * MarketingAttribution (undefined fields + epoch capturedAt) for the BFF schema.
 */
function toMarketingAttribution(
  sdk: SdkMarketingAttribution | null,
): MarketingAttribution | undefined {
  if (!sdk) return undefined;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(sdk)) {
    if (value === null) continue;
    if (key === 'capturedAt' && typeof value === 'string') {
      out[key] = Date.parse(value);
    } else {
      out[key] = value;
    }
  }
  return out as MarketingAttribution;
}

/**
 * Meta/GA first-party IDs the dealer's pixels set on their own origin, read live
 * at handoff. checkout.ekho.com is a different eTLD+1 and can't read these
 * cookies, so we forward them via query string (see `appendAttribution`). Gated
 * on consent: these are third-party-analytics identifiers, so they only forward
 * once tracking is allowed — unlike the SDK-owned identity IDs, which forward in
 * every consent state.
 */
function readLiveMarketingIds(): Pick<SdkMarketingAttribution, 'fbp' | 'fbc' | 'gaClientId'> {
  if (!isTrackingAllowed()) return { fbp: null, fbc: null, gaClientId: null };
  return {
    fbp: readCookie('_fbp'),
    fbc: readCookie('_fbc'),
    gaClientId: parseGaClientId(readCookie('_ga')),
  };
}

/**
 * First-touch UTMs/click IDs stay on the bootstrap snapshot; identity IDs and
 * Meta/GA IDs are re-read at handoff. Forwards `ekho_aid` / `ekho_sid` to
 * checkout in every consent state: checkout is a US-only surface that sets its
 * own cookies on landing, so the identity handoff is consent-independent. Before
 * consent the identity IDs resolve in-memory (no cookie on the dealer's origin);
 * after grant they come from the persisted cookies. The Meta/GA IDs
 * (`fbp` / `fbc` / `gaClientId`) are consent-gated inside `readLiveMarketingIds`.
 */
export function withLiveIdentity(
  attribution: SdkMarketingAttribution | null,
): SdkMarketingAttribution | null {
  if (!attribution) return null;
  const { anonymousId, sessionId } = getConsentedIdentity();
  return {
    ...attribution,
    ...readLiveMarketingIds(),
    anonymousId,
    sessionId,
  };
}

/** Append non-null attribution params to a URL. URL-wins on conflicts. */
export function appendAttribution(
  checkoutUrl: string,
  attribution: SdkMarketingAttribution | null,
  /** When set, forwards this code instead of re-reading the live page URL. */
  affiliateCode?: string | null,
): string {
  if (!attribution) return checkoutUrl;

  const url = new URL(checkoutUrl);
  const fields: Array<[string, string | null]> = [
    ['utm_source', attribution.utmSource],
    ['utm_medium', attribution.utmMedium],
    ['utm_campaign', attribution.utmCampaign],
    ['utm_content', attribution.utmContent],
    ['utm_term', attribution.utmTerm],
    ['utm_id', attribution.utmId],
    ['gclid', attribution.gclid],
    ['gbraid', attribution.gbraid],
    ['wbraid', attribution.wbraid],
    ['fbclid', attribution.fbclid],
    ['msclkid', attribution.msclkid],
    ['ttclid', attribution.ttclid],
    ['referrer', attribution.referrer],
    ['landing_page', attribution.landingPage],
    // Meta/GA first-party IDs read on the dealer's origin (see
    // `readLiveMarketingIds`). Forwarded so checkout can carry them onto the
    // cart's marketingAttribution, which the buyer-portal Purchase pixel reads
    // back to attribute to the original ad click. `client_id` is the GA4 param
    // name the dealer side expects.
    ['fbp', attribution.fbp],
    ['fbc', attribution.fbc],
    ['client_id', attribution.gaClientId],
    // Cross-domain handoff of the identity IDs — cookies set on the dealer's
    // origin aren't visible to checkout.ekho.com (different eTLD+1), so we
    // forward via query string. The checkout page is expected to re-write
    // these into its own `ekho_aid` / `ekho_sid` cookies on landing so the
    // session continues uninterrupted.
    ['ekho_aid', attribution.anonymousId],
    ['ekho_sid', attribution.sessionId],
  ];

  for (const [key, value] of fields) {
    if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
  }

  const code = affiliateCode === undefined ? readAffiliateCode() : affiliateCode;
  if (code && !url.searchParams.has('ref')) url.searchParams.set('ref', code);

  return url.toString();
}

/** Public helper for partners whose backend mints the checkout URL. */
export function attachAttributionToUrl(url: string, config: CheckoutClientConfig): string {
  return appendAttribution(url, withLiveIdentity(config.attribution));
}

/** Expand a single vehicle + quantity into the vehicles array the BFF expects. */
function buildVehiclesArray(options: SdkCreateCartOptions): SdkCartVehicle[] {
  if (!options.vehicle) return [];
  const count = Math.max(1, Math.floor(options.quantity ?? 1));
  return Array.from({ length: count }, () => options.vehicle!);
}

export interface CheckoutClientConfig {
  svpId: string;
  client: SdkApiClient;
  attribution: SdkMarketingAttribution | null;
  /** Vehicles whose availability was already reported on the current page. */
  reportedAvailability: Set<string>;
}

/**
 * Create a checkout cart (or configurator handoff) via the SDK BFF.
 * Non-navigational — returns `{ kind, url }`; caller may `redirect(url)`.
 */
/** What every cart POST carries, whichever route shaped the body. */
async function postCart(
  endpoint: string,
  body: Record<string, unknown>,
  config: CheckoutClientConfig,
  isPreorder: boolean,
): Promise<SdkCreateCartResult> {
  track(EKHO_EVENTS.SDK_BUY_ONLINE_CLICKED, { isPreorder });
  const live = withLiveIdentity(config.attribution);
  const affiliateCode = readAffiliateCode();
  const { marketingAttribution, ...rest } = body;
  const result = await config.client.post<SdkCreateCartResult>(endpoint, {
    svpId: config.svpId,
    ...rest,
    marketingAttribution: marketingAttribution ?? toMarketingAttribution(live),
    ...(affiliateCode ? { affiliateCode } : {}),
  });
  // Reuse the code captured for the create body so a SPA route change during
  // the request cannot switch the checkout URL to a different affiliate.
  return {
    checkoutUrl: appendAttribution(result.checkoutUrl, live, affiliateCode),
    // Carried through rather than dropped: the cart was minted without these,
    // and the caller's page is the only place that can tell the buyer.
    droppedAccessories: result.droppedAccessories ?? [],
  };
}

/** A dry run creates nothing, so it reports no buy click and decorates no URL. */
function postValidate(
  endpoint: string,
  body: Record<string, unknown>,
  config: CheckoutClientConfig,
): Promise<SdkAvailabilityResult> {
  return config.client.post<SdkAvailabilityResult>(endpoint, {
    svpId: config.svpId,
    ...body,
    validate_only: true,
  });
}

/**
 * Create a checkout cart (or configurator handoff) via the SDK BFF.
 * Non-navigational — returns `{ kind, url }`; caller may `redirect(url)`.
 */
export async function createCart(
  options: SdkCreateCartOptions,
  config: CheckoutClientConfig,
): Promise<SdkCreateCartResult> {
  const { vehicle, quantity, isPreorder, ...rest } = options;
  void vehicle;
  void quantity;
  return postCart(
    cartEndpoints.create,
    {
      ...rest,
      vehicles: buildVehiclesArray(options),
      cancelUrl: options.cancelUrl ?? window.location.href,
      ...(isPreorder && { orderCreationType: OrderCreationType.ANTE_PREORDER }),
    },
    config,
    isPreorder === true,
  );
}

/** The cancel URL every cart falls back to: the page the buyer left. */
function withCancelUrl<T extends { cancel_redirect_url?: string }>(
  options: T,
): Record<string, unknown> {
  return { ...options, cancel_redirect_url: options.cancel_redirect_url ?? window.location.href };
}

/** A cart for one unit already in stock. */
export function createInventoryCart(
  options: SdkInventoryCartOptions,
  config: CheckoutClientConfig,
): Promise<SdkCreateCartResult> {
  return postCart(cartEndpoints.inventory, withCancelUrl(options), config, false);
}

/** A cart for one vehicle to be built. */
export function createBuildToOrderCart(
  options: SdkBuildToOrderCartOptions,
  config: CheckoutClientConfig,
): Promise<SdkCreateCartResult> {
  return postCart(
    cartEndpoints.buildToOrder,
    withCancelUrl(options),
    config,
    options.is_preorder === true,
  );
}

export function validateInventoryCart(
  options: SdkValidateCartOptions<SdkInventoryCartOptions>,
  config: CheckoutClientConfig,
): Promise<SdkAvailabilityResult> {
  return postValidate(cartEndpoints.inventory, options, config);
}

export function validateBuildToOrderCart(
  options: SdkValidateCartOptions<SdkBuildToOrderCartOptions>,
  config: CheckoutClientConfig,
): Promise<SdkAvailabilityResult> {
  return postValidate(cartEndpoints.buildToOrder, options, config);
}

/** Mirrors the BFF, which resolves on the URL when both identifiers are passed. */
function availabilityIdentifier(
  options: SdkAvailabilityOptions,
): { type: SdkAvailabilityIdentifierType; key: string } | null {
  if (options.vehicleDetailsUrl) {
    return {
      type: SDK_AVAILABILITY_IDENTIFIER_TYPES.VEHICLE_DETAILS_URL,
      key: options.vehicleDetailsUrl,
    };
  }
  const productId = options.vehicle?.productId;
  if (!productId) return null;
  const accessories = [...(options.vehicle?.accessories ?? [])].sort().join(',');
  return { type: SDK_AVAILABILITY_IDENTIFIER_TYPES.PRODUCT_ID, key: `${productId}|${accessories}` };
}

/**
 * Report each distinct verdict once per vehicle per page. The dedupe key holds
 * the outcome and whether a ZIP was sent, so a retry that succeeds, or a ZIP
 * check that flips the answer to out-of-market, still reports. Only the outcome
 * and the shape of the request are sent: the ZIP and the identifier values stay
 * out, because these events run on dealer sites and the identifiers can carry
 * query strings.
 */
function reportAvailability(
  options: SdkAvailabilityOptions,
  config: CheckoutClientConfig,
  result: SdkAvailabilityResult | null,
): void {
  const identifier = availabilityIdentifier(options);
  const outcome = result
    ? `${String(result.available)}:${result.reason ?? ''}`
    : SDK_AVAILABILITY_REPORTED_REASONS.ERRORED;
  const dedupeKey = `${outcome}|${options.zip ? 'zip' : 'nozip'}|${identifier?.type ?? ''}|${identifier?.key ?? ''}`;
  if (config.reportedAvailability.has(dedupeKey)) return;
  config.reportedAvailability.add(dedupeKey);
  trackEventBeacon(EKHO_EVENTS.SDK_PURCHASE_AVAILABILITY_RECEIVED, {
    available: result?.available === true,
    reason: result ? result.reason : SDK_AVAILABILITY_REPORTED_REASONS.ERRORED,
    hasZip: Boolean(options.zip),
    identifierType: identifier?.type,
  });
}

/** Check if a vehicle is available for purchase. */
export async function isAvailable(
  options: SdkAvailabilityOptions,
  config: CheckoutClientConfig,
): Promise<SdkAvailabilityResult> {
  let result: SdkAvailabilityResult;
  try {
    result = await config.client.post<SdkAvailabilityResult>(cartEndpoints.available, {
      svpId: config.svpId,
      ...options,
    });
  } catch (err) {
    reportAvailability(options, config, null);
    throw err;
  }
  reportAvailability(options, config, result);
  return result;
}

/** Navigate the browser to the given URL. */
export function redirect(url: string): void {
  window.location.href = url;
}
